var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk } from '@reduxjs/toolkit';
import { MathUtils } from '@printu/geom';
import { getItemBounds } from 'app/core/markers/bounds';
import { EditorActions } from 'app/stores/editor/actions';
import { Prompt } from 'app/stores/editor/prompt';
import { HistoryActions } from 'app/stores/history/actions';
import { parseImageData } from 'app/stores/image/actions/parse';
import { persistImage } from 'app/stores/image/actions/persist';
import { projectSlice } from 'app/stores/project';
import { createPage } from 'app/stores/project/page';
import { createImageMarkerFromState } from 'app/views/components/editor/tools/insert/InsertImage';
import { isSupportedDataSource } from 'app/utils/upload';
import { getMarker } from '../../selectors';
import { deleteItems } from './deleteItems';
import { selectPage } from './selectPage';
const createPageFromImage = (image, dispatch) => __awaiter(void 0, void 0, void 0, function* () {
    // add page
    const page = createPage({ image: image.id });
    dispatch(projectSlice.actions.addPage({ page }));
    // select page
    yield dispatch(selectPage(page.id));
});
export const createFromImageData = createAsyncThunk('project/createFromImageDate', (params, { dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    const { data, source } = params;
    try {
        // load and register image
        const image = yield dispatch(parseImageData({ data, source, name: data === null || data === void 0 ? void 0 : data.name })).unwrap();
        dispatch(projectSlice.actions.addImage({ image }));
        // create and add page
        yield createPageFromImage(image, dispatch);
        // persist image
        yield dispatch(persistImage(data));
    }
    catch (err) {
        getLogger('ImageLoad', 'error')(err);
    }
}));
export const addPageFromImageData = createAsyncThunk('image/loadFile', (params, { dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    const { data, source } = params;
    const supportedSources = data.filter(dataSource => isSupportedDataSource(dataSource));
    if (!supportedSources.length) {
        throw new Error('Invalid source');
    }
    try {
        const multiSelectEnabled = configGet('multipleFilesSelectEnabled', false);
        if (!multiSelectEnabled) {
            yield dispatch(EditorActions.closeProject());
            yield dispatch(createFromImageData({
                data: supportedSources[0],
                source,
            }));
            dispatch(HistoryActions.clear());
            return;
        }
        dispatch(HistoryActions.record());
        for (let index = 0; index < supportedSources.length; index += 1) {
            const dataSource = supportedSources[index];
            try {
                // eslint-disable-next-line no-await-in-loop
                yield dispatch(createFromImageData({
                    data: dataSource,
                    source,
                }));
            }
            catch (err) {
                getLogger('ImageLoad', 'error')(err);
            }
        }
        dispatch(HistoryActions.commit());
    }
    catch (err) {
        dispatch(EditorActions.showToast({
            message: 'Error loading image',
            variant: 'negative',
        }));
        getLogger('ImageLoad', 'error')(err);
    }
}));
export const updateMarkerFromImageData = createAsyncThunk('project/createMarkerFromImageData', (params, { dispatch, getState }) => __awaiter(void 0, void 0, void 0, function* () {
    const { markerId, data, source, clearTool = true, selectMarker = true, } = params;
    try {
        const state = getState();
        const marker = getMarker(state, markerId);
        if (!marker) {
            return false;
        }
        // load and register image
        const image = yield dispatch(parseImageData({ data, source, name: data === null || data === void 0 ? void 0 : data.name })).unwrap();
        dispatch(projectSlice.actions.addImage({ image }));
        const bounds = getItemBounds(marker);
        const scale = MathUtils.getFitScale(image.size.width, image.size.height, bounds.width, bounds.height);
        // update marker
        dispatch(projectSlice.actions.itemUpdate({
            id: markerId,
            patch: {
                image: image.id,
                endX: image.size.width * scale,
                endY: image.size.height * scale,
            },
            undoable: false,
        }));
        if (clearTool) {
            dispatch(EditorActions.clearTool());
        }
        if (selectMarker) {
            dispatch(projectSlice.actions.itemSelect({ id: marker.id }));
        }
        return true;
    }
    catch (err) {
        dispatch(deleteItems({ ids: [markerId] }));
        dispatch(EditorActions.showToast({
            message: 'Error loading image',
            variant: 'negative',
        }));
        getLogger('ImageLoad', 'error')(err);
        return false;
    }
}));
export const createMarkersFromImageData = createAsyncThunk('project/createMarkerFromImageData', (params, { dispatch, getState }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { data, source } = params;
    try {
        const supportedSources = data.filter(dataSource => isSupportedDataSource(dataSource));
        if (!supportedSources.length) {
            throw new Error('Invalid source');
        }
        let marker;
        for (let index = 0; index < supportedSources.length; index += 1) {
            const dataSource = supportedSources[index];
            try {
                const bounds = getItemBounds(marker);
                const state = getState();
                marker = createImageMarkerFromState({
                    x: 10 + index * ((_a = bounds === null || bounds === void 0 ? void 0 : bounds.width) !== null && _a !== void 0 ? _a : 0) * 0.25,
                    y: 10 + index * ((_b = bounds === null || bounds === void 0 ? void 0 : bounds.height) !== null && _b !== void 0 ? _b : 0) * 0.25,
                }, state);
                dispatch(projectSlice.actions.itemInsert({
                    marker,
                    undoable: true,
                }));
                // eslint-disable-next-line no-await-in-loop
                const { payload: result } = yield dispatch(updateMarkerFromImageData({
                    markerId: marker.id,
                    data: dataSource,
                    source,
                    clearTool: false,
                    selectMarker: false,
                }));
                if (!result) {
                    marker = undefined;
                }
            }
            catch (err) {
                getLogger('ImageLoad', 'error')(err);
            }
        }
        dispatch(EditorActions.clearTool());
        if (marker) {
            dispatch(projectSlice.actions.itemSelect({ id: marker.id }));
        }
    }
    catch (err) {
        getLogger('ImageLoad', 'error')(err);
    }
}));
export const handleImageFilesDrop = createAsyncThunk('project/imageDrop', (params, { dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    if (!params.data.length) {
        return;
    }
    if (params.source !== 'file') {
        return;
    }
    const addSlidesOnImageDrop = configGet('addSlidesOnImageDrop', false);
    let action = 'create-slides';
    if (!addSlidesOnImageDrop) {
        const isAddingMultipleFiles = params.data.length > 1;
        const title = isAddingMultipleFiles ? 'Add images' : 'Add image';
        const message = 'Do you want to add the selected image(s) to the project\nas new slides or as markers to current slide?';
        const promptResult = yield dispatch(Prompt.show({
            title,
            content: message,
            variant: 'prompt',
            options: [
                { id: 'create-markers', label: 'Markers' },
                { id: 'create-slides', label: 'Slides' },
            ],
        })).unwrap();
        if (promptResult === 'cancel') {
            return;
        }
        action = promptResult;
    }
    switch (action) {
        case 'create-slides':
            yield dispatch(addPageFromImageData(params));
            break;
        case 'create-markers':
            yield dispatch(createMarkersFromImageData(params));
            break;
        default:
            break;
    }
}));
